import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { BarChart3, Clock, Package, RefreshCw, Trash2 } from 'lucide-react'

import { EID } from '@/lib/element-ids.registry'
import { useDeleteListingAudit } from '@/hooks/useListingBuilder'
import type { ListingAuditListItem } from '@/lib/api/listing-builder.types'

/** Repräsentativer Score: bevorzugt `overall`, sonst der erste numerische Wert. */
function pickScore(scores: Record<string, number> | null): number | null {
  if (!scores) return null
  if (typeof scores.overall === 'number') return scores.overall
  const first = Object.values(scores).find((v) => typeof v === 'number')
  return first ?? null
}

const scoreColor = (s: number) =>
  s >= 80 ? 'text-green-500' : s >= 60 ? 'text-yellow-500' : 'text-red-500'

const PENDING = new Set(['pending', 'queued'])
const RUNNING = new Set(['running', 'scraping', 'analyzing'])

const BADGE_BASE = 'rounded-full px-2 py-0.5 text-xs font-semibold'

const REMAKE_STATUS_COLORS: Record<string, string> = {
  none: 'bg-neutral-100 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400',
  pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  running: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
  completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
}

/** Status-Badge für laufende/wartende/fehlgeschlagene Audits (null = fertig). */
function statusBadge(status: string): { key: string; cls: string } | null {
  if (PENDING.has(status))
    return { key: 'listingBuilder.overview.pending', cls: 'bg-neutral-200 text-neutral-700 dark:bg-neutral-600 dark:text-neutral-200' }
  if (RUNNING.has(status))
    return { key: 'listingBuilder.overview.inProgress', cls: 'animate-pulse bg-primary-500 text-white' }
  if (status === 'failed') return { key: 'listingBuilder.overview.failed', cls: 'bg-red-500 text-white' }
  return null
}

function CardBadges({ audit }: { audit: ListingAuditListItem }) {
  const { t } = useTranslation()
  const deleteAudit = useDeleteListingAudit()
  const badge = statusBadge(audit.status)
  const remake = audit.remake_status ?? 'none'
  return (
    <div className="flex shrink-0 items-center gap-1">
      {badge ? (
        <span className={`${BADGE_BASE} ${badge.cls}`}>{t(badge.key)}</span>
      ) : audit.is_new ? (
        <span className={`${BADGE_BASE} animate-pulse bg-orange-500 text-white`}>{t('listingBuilder.overview.new')}</span>
      ) : (
        <span className={`${BADGE_BASE} bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400`}>
          {t('listingBuilder.overview.completed')}
        </span>
      )}
      {remake !== 'none' && (
        <span className={`inline-flex items-center gap-1 ${BADGE_BASE} ${REMAKE_STATUS_COLORS[remake] ?? REMAKE_STATUS_COLORS.none}`}>
          <RefreshCw className="h-3 w-3" />
          {t(`listingBuilder.overview.remakeStatus.${remake}`)}
        </span>
      )}
      <button
        id={`${EID['listing-builder-card-delete-btn']}-${audit.id}`}
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          if (window.confirm(t('listingBuilder.overview.deleteConfirm'))) deleteAudit.mutate(audit.id)
        }}
        aria-label={t('listingBuilder.overview.delete')}
        className="rounded p-1 text-neutral-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-500 group-hover:opacity-100 dark:hover:bg-red-900/20"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

function CardMeta({ audit }: { audit: ListingAuditListItem }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
      <span className="font-medium uppercase">
        {t(`amazonListing.marketplaces.${audit.market_tld}`, { defaultValue: audit.market_tld.toUpperCase() })}
      </span>
      {audit.asin && (
        <span className="rounded bg-neutral-100 px-2 py-0.5 font-mono font-medium text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300">
          {audit.asin}
        </span>
      )}
      {audit.created_at && (
        <>
          <Clock className="h-3 w-3" />
          <span>{new Date(audit.created_at).toLocaleDateString()}</span>
        </>
      )}
      {audit.audit_count > 1 && (
        <span className="rounded bg-primary-100 px-1.5 py-0.5 font-medium text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
          {t('listingBuilder.overview.versionCount', { count: audit.audit_count })}
        </span>
      )}
    </div>
  )
}

export function ListingAuditCard({ audit }: { audit: ListingAuditListItem }) {
  const navigate = useNavigate()
  const score = pickScore(audit.scores)
  const title = audit.product_title || audit.source_ref
  const open = () => navigate(`/v2/tools/listing-builder/${audit.id}`)

  return (
    <div
      role="button"
      tabIndex={0}
      id={`${EID['listing-builder-audit-card']}-${audit.id}`}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          open()
        }
      }}
      className="group w-full cursor-pointer rounded-xl border border-neutral-200 bg-white p-4 text-left transition-all hover:border-orange-300 hover:shadow-md dark:border-neutral-700 dark:bg-neutral-800 dark:hover:border-orange-600"
    >
      <div className="flex gap-3">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-neutral-100 dark:bg-neutral-700">
          {audit.image_url ? (
            <img src={audit.image_url} alt={title} className="h-full w-full object-contain" />
          ) : (
            <Package className="h-6 w-6 text-neutral-400" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-start justify-between gap-2">
            <CardMeta audit={audit} />
            <CardBadges audit={audit} />
          </div>
          <p className="mb-1 truncate text-sm font-medium text-neutral-800 dark:text-neutral-200">{title}</p>
          {score != null && (
            <div className="flex items-center gap-1.5 text-xs text-neutral-500">
              <BarChart3 className="h-3.5 w-3.5 text-neutral-400" />
              <span className={`text-base font-bold ${scoreColor(score)}`}>{score}</span>
              <span className="text-neutral-400">/100</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
